
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Loader2,
  MapPin,
  Phone,
  Truck,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CompletionForm } from '../components/CompletionForm';
import { OfflineBar } from '../components/OfflineBar';
import { PhotoGallery } from '../components/PhotoGallery';
import { useFieldJob } from '../lib/use-field-job';

/**
 * The technician's screen. UI.md §3: one hand, outdoors, in sunlight.
 *
 * Large tap targets, high contrast, the primary action at the bottom within
 * thumb reach. Assume gloves, rain and a cracked screen.
 */
const PRIMARY: Record<string, { label: string; icon: typeof Truck }> = {
  acknowledge: { label: 'Acknowledge', icon: Check },
  start_travel: { label: 'Start travel', icon: Truck },
  arrive: { label: "I've arrived", icon: MapPin },
  begin_work: { label: 'Start work', icon: Wrench },
  complete: { label: 'Complete job', icon: CheckCircle2 },
};

const DECLINE_REASONS: Array<[string, string]> = [
  ['ON_ANOTHER_JOB', 'On another job'],
  ['NO_TRANSPORT', 'No transport'],
  ['NO_MATERIALS', 'No materials'],
  ['NO_TOOLS', 'No tools'],
  ['NOT_CERTIFIED', 'Not certified for this'],
  ['WEATHER_UNSAFE', 'Unsafe in this weather'],
  ['SECURITY_CONCERN', 'Security concern'],
  ['SICK', 'Sick'],
  ['OTHER', 'Other'],
];

export function FieldPage() {
  const { assignmentId = '' } = useParams();
  const [panel, setPanel] = useState<'decline' | 'complete' | null>(null);
  const [photoCount, setPhotoCount] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);

  const { view, loading, error, online, queued, busy, photos, doneLocally, act, addPhoto, syncNow } =
    useFieldJob(assignmentId);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="mx-auto max-w-sm px-5 py-20 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-danger-500" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-xl">{error ?? 'Could not open this job'}</h1>
        <p className="mt-2 text-muted-foreground">Check with dispatch.</p>
      </div>
    );
  }

  const { job } = view;
  // Hide an action the technician has already taken but that has not reached
  // the server yet — offering "Arrived" twice is how you get a confused tap.
  const actions = view.actions.filter((a) => !doneLocally.includes(a));
  const primary = actions.filter((a) => a in PRIMARY && a !== 'acknowledge');
  const canAcknowledge = actions.includes('acknowledge');
  const canDecline = actions.includes('decline');

  return (
    <div className="mx-auto min-h-screen max-w-lg bg-background pb-40">
      <header className="gradient-hero px-5 py-5 text-white">
        <div className="flex items-center justify-between">
          <img src="/brand/logo.png" alt="GoSpeedNet" className="h-7 w-auto rounded bg-white p-1" />
          <span className="rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold">
            {job.priority}
          </span>
        </div>
        <h1 className="mt-4 font-heading text-2xl">{job.reference}</h1>
        <p className="mt-1 text-white/90">{job.title}</p>
        <p className="mt-2 text-sm text-white/60">
          {job.jobType} · {job.status.replace(/_/g, ' ').toLowerCase()}
        </p>
      </header>

      <OfflineBar online={online} queued={queued} busy={busy} onSync={() => void syncNow()} />

      <main className="space-y-4 px-5 py-5">
        {view.acknowledged && (
          <p className="flex items-center gap-2 rounded-xl bg-secondary-50 p-3 text-sm text-secondary-700">
            <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
            Acknowledged — dispatch knows you have this.
          </p>
        )}

        {job.site && (
          <section className="card">
            <h2 className="font-heading text-lg">Where</h2>
            <p className="mt-2 text-lg font-medium">{job.site.houseNo ?? job.site.label}</p>
            {job.area && <p className="text-muted-foreground">{job.area}</p>}
            {job.site.landmark && <p className="mt-1 text-muted-foreground">{job.site.landmark}</p>}

            {job.site.accessNotes && (
              <p className="mt-3 rounded-xl bg-accent-50 p-3 text-accent-600">
                <strong>Access:</strong> {job.site.accessNotes}
              </p>
            )}

            {/*
              Hands the address to whatever map app the phone already has, rather
              than embedding one: no API key, no tiles to load on a slow
              connection, and it opens the app the technician already knows.
              Coordinates when the site has them, otherwise the words — which is
              what they would have typed anyway.
            */}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                job.site.lat && job.site.lng
                  ? `${job.site.lat},${job.site.lng}`
                  : [job.site.houseNo, job.site.landmark, job.area, 'Kisii'].filter(Boolean).join(', '),
              )}`}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary mt-3 w-full py-3 text-base"
            >
              <MapPin className="h-5 w-5" aria-hidden="true" />
              Navigate there
            </a>

            {job.site.contactPhone && (
              <a
                href={`tel:${job.site.contactPhone}`}
                className="btn-secondary mt-3 w-full py-3 text-base"
              >
                <Phone className="h-5 w-5" aria-hidden="true" />
                Call {job.site.contactName ?? 'site contact'}
              </a>
            )}
          </section>
        )}

        {job.customer && (
          <section className="card">
            <h2 className="font-heading text-lg">Client</h2>
            <p className="mt-1 text-lg font-medium">{job.customer.name}</p>
            <a href={`tel:${job.customer.phone}`} className="btn-secondary mt-3 w-full py-3 text-base">
              <Phone className="h-5 w-5" aria-hidden="true" />
              {job.customer.phone}
            </a>
            {job.customer.alt && (
              <a
                href={`tel:${job.customer.alt}`}
                className="mt-2 block text-center text-sm text-muted-foreground underline"
              >
                Try {job.customer.alt}
              </a>
            )}
          </section>
        )}

        {job.element && (
          <section className="card">
            <h2 className="font-heading text-lg">Equipment</h2>
            <p className="mt-1">
              {job.element.name}
              <span className="label-mono ml-2 text-xs text-muted-foreground">
                {job.element.type}
              </span>
            </p>
          </section>
        )}

        {job.description && (
          <section className="card">
            <h2 className="font-heading text-lg">Notes</h2>
            <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{job.description}</p>
          </section>
        )}

        
        {panel === 'decline' && (
          <section className="card">
            <h2 className="font-heading text-lg">Why can&apos;t you take it?</h2>
            <div className="mt-3 space-y-2">
              {DECLINE_REASONS.map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => void act({ event: 'decline', declineReasonCode: code })}
                  disabled={busy}
                  className="w-full rounded-xl border border-border bg-card px-4 py-3 text-left text-base hover:bg-muted"
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
        )}

        {panel === 'complete' && (
          <CompletionForm
            schema={job.completionSchema ?? []}
            requiredEvidence={job.requiredEvidence ?? {}}
            photoCount={photoCount}
            busy={busy}
            errors={errors}
            onPhoto={async (file) => {
              try {
                await addPhoto(file);
                // Counted locally: the photo is on the phone and will upload,
                // so the evidence rule should not block a technician who is
                // standing in a cabinet with no signal.
                setPhotoCount((current) => current + 1);
                setErrors([]);
              } catch (caught) {
                setErrors([(caught as Error).message]);
              }
            }}
            catalogue={view.materialCatalogue ?? []}
            categoryLabels={view.categoryLabels ?? {}}
            initialMaterials={(job.materials ?? []).map((m) => ({
              code: m.code,
              quantity: m.quantity,
              serial: m.serial ?? undefined,
              note: m.note ?? undefined,
            }))}
            initialMaterialsOther={job.materialsOther ?? ''}
            onSiteAt={job.onSiteAt ?? null}
            onSubmit={(payload) => void act({ event: 'complete', ...payload })}
          />
        )}

        {/*
          What is already on this job. Useful on a repeat fault — a technician
          can see how the last visit left the box before opening it — and as a
          check that the photo just taken was not a thumb over the lens.
        */}
        {photos.length > 0 && (
          <section className="mt-4">
            <h2 className="mb-2 text-sm font-medium text-muted-foreground">
              Photos on this job ({photos.length})
            </h2>
            <PhotoGallery
              attachments={photos}
              urlFor={(attachmentId) => `/t/${assignmentId}/photos/${attachmentId}`}
            />
          </section>
        )}
      </main>

      {/* Primary action pinned within thumb reach — UI.md §3. */}
      {(canAcknowledge || primary.length > 0 || canDecline) && !panel && (
        <div className="fixed inset-x-0 bottom-0 mx-auto max-w-lg space-y-2 border-t border-border bg-card p-4">
          {canAcknowledge && (
            <button
              type="button"
              onClick={() => void act({ event: 'acknowledge' })}
              disabled={busy}
              className="btn-primary w-full py-4 text-base"
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                <Check className="h-5 w-5" aria-hidden="true" />
              )}
              Acknowledge
            </button>
          )}

          {primary.map((event) => {
            const { label, icon: Icon } = PRIMARY[event]!;
            return (
              <button
                key={event}
                type="button"
                onClick={() => (event === 'complete' ? setPanel('complete') : void act({ event }))}
                disabled={busy}
                className="btn-primary w-full py-4 text-base"
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
                {label}
              </button>
            );
          })}

          {canDecline && (
            <button
              type="button"
              onClick={() => setPanel('decline')}
              className="btn-secondary w-full py-3 text-base"
            >
              Can&apos;t take this
            </button>
          )}
        </div>
      )}
    </div>
  );
}
